import { gql, useQuery } from "@apollo/client";
import type { IMediaMetaObjectQuery } from "@repo/shopify/queries/metaobject-gql";
import { mediaMetaobjectQuery } from "@repo/shopify/queries/metaobject-gql";
import type {
  Image as ImageType,
  MediaImage,
  Metaobject,
  MetaobjectEdge,
} from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import type { ISortOption } from "@const/constant";
import { SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import { useLayoutStore } from "@store/layout";

export default function Stores() {
  const { openToast, setMeta, showFooter } = useLayoutStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort") || "new";

  const [sort, setSort] = useState<ISortOption>(sortParam === "old" ? SORT[0] : SORT[1]);

  const variables = useMemo(() => ({ type: "store", first: 50, sort: sort.value, reverse: sort.reverse }), [sort]);

  const { data, loading } = useQuery<IMediaMetaObjectQuery>(gql(mediaMetaobjectQuery.query), {
    variables,
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setMeta({ title: `Stores - ${DEFAULT_TITLE}` });
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const getValue = (node: Metaobject, key: string) => {
    return node.fields.find((item) => item.key === key);
  };

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <>
          <section className="pc:col-span-12 col-span-8 flex justify-between">
            <h2 className="text-title">Stores</h2>
            <Button
              as="text"
              onClick={() => {
                const sortIndex = SORT.findIndex((option) => option.label === sort.label);
                setSort(SORT[sortIndex === 0 ? 1 : 0]);
                setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
              }}
            >
              {sort.label}
            </Button>
          </section>

          <section className="pc:col-span-12 col-span-8 flex flex-col justify-between gap-10">
            {data.metaobjects.edges.map(({ node: store }: MetaobjectEdge) => {
              const images = getValue(store, "images")?.references?.edges.map(
                ({ node }) => (node as MediaImage).image,
              ) as ImageType[];

              return (
                <article
                  className="tnt-gap-x pc:col-span-12 pc:grid-cols-12 pc:gap-y-0 col-span-8 grid grid-cols-8 gap-y-2.5"
                  key={store.handle}
                >
                  <Link className="pc:col-span-6 col-span-8" href={`${URLS.STORES}/${store.handle}`} state={store.id}>
                    <Image height={images[0].height || 2000} src={images[0].url} width={images[0].width || 2500} />
                  </Link>
                  <div className="pc:col-span-5 col-span-8 flex flex-col gap-5">
                    <Link
                      className="text-title font-features-normal flex! flex-col whitespace-pre-line"
                      href={`${URLS.STORES}/${store.handle}`}
                      state={store.id}
                    >
                      {(getValue(store, "title")?.value || "").replace(/\\n/g, "\n")}
                    </Link>
                    <div className="tnt-gap-x grid grid-cols-6">
                      <span className="pc:col-span-1 col-span-2">Address</span>
                      <Link
                        as="text"
                        className="pc:col-span-5 col-span-4 w-fit"
                        href={(JSON.parse(getValue(store, "location")?.value || "[]") as [string, string])[1] || ""}
                      >
                        {(JSON.parse(getValue(store, "location")?.value || "[]") as [string, string])[0] || ""}
                      </Link>
                      <span className="pc:col-span-1 col-span-2">Phone</span>
                      <Link
                        as="text"
                        className="pc:col-span-5 col-span-4 w-fit"
                        href={`tel:${getValue(store, "tel")?.value || ""}`}
                      >
                        {getValue(store, "tel")?.value || ""}
                      </Link>

                      <span className="pc:col-span-1 col-span-2">Opening Hours</span>
                      <span className="pc:col-span-5 col-span-4 w-fit whitespace-pre-line">
                        {(getValue(store, "opening_hour")?.value || "").replace(/\\n/g, "\n")}
                      </span>
                    </div>
                    <Link as="text" className="w-fit" href={`${URLS.STORES}/${store.handle}`} state={store.id}>
                      More Info →
                    </Link>
                  </div>
                </article>
              );
            })}
          </section>
        </>
      )}
    </>
  );
}
